import { createSlice } from "@reduxjs/toolkit";
import {
  AllMembersType,
  ChatConversation,
  ChatInitialState,
} from "../../Types/Chat.type";
import { getUserID } from "../../services/helper";

const currentUserId = Number(getUserID());
const initialState: ChatInitialState = {
  isLoading: false,
  allMembers: [],
  members: [],
  chats: [],
  isGroup: false,
  selectedGroup: null,
  groups: [],
  chatContact: [],
  currentUser: {},
  selectedUser: [],
  toggleSideBar: false,
  socket: null,
  connected: false,
  connectionId: "",
  friends: [],
  pending_friends: [],
  blocked: [],
  stores: [],
  store_chat_requests: [],
  unreadMessageCount: 0,
};

const ChatSlice = createSlice({
  name: "chatBox",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setToggleSideBar: (state, action) => {
      state.toggleSideBar = action.payload;
    },
    setAllMembers: (state, action) => {
      state.allMembers = action.payload;
      state.members = action.payload;
    },
    setStores: (state, action) => {
      state.stores = action.payload;
    },
    setStoreChatRequests: (state, action) => {
      state.store_chat_requests = action.payload;
    },
    setSocket: (state, action) => {
      state.socket = action.payload;
    },
    setConnected: (state, action) => {
      state.connected = action.payload;
    },
    setConnectionId: (state, action) => {
      state.connectionId = action.payload;
    },
    setChats: (state, action) => {
      state.chats = action.payload;
    },
    clearChat: (state, action) => {
      // Find the chat and clear its messages
      state.chats = state.chats.map((chat) =>
        chat.id === action.payload ? { ...chat, messages: [] } : chat
      );
    },

    deleteChat: (state, action) => {
      // Filter out the chat to delete it completely
      console.log(action.payload);
      state.chats = state.chats.filter((chat) => chat.id !== action.payload);
    },

    setGroups: (state, action) => {
      state.groups = action.payload;
    },
    UpdateFriends: (state, action) => {
      state.friends = action.payload;
    },
    UpdateRequests: (state, action) => {
      state.pending_friends = action.payload;
    },
    UpdateBlocked: (state, action) => {
      state.blocked = action.payload;
    },
    newGroupAdded: (state, action) => {
      state.groups = [action.payload, ...state.groups];
    },
    updateGroup: (state, action) => {
      const { groupId, groupName, users } = action.payload;
      const groupIndex = state.groups.findIndex(
        (group) => group.groupId === groupId
      );
      if (groupIndex !== -1) {
        state.groups[groupIndex].groupName = groupName;
        state.groups[groupIndex].users = users;
      }
    },
    deleteGroup: (state, action) => {
      console.log(action.payload);
      state.groups = state.groups.filter(
        (group) => group.groupId !== action.payload
      );
    },
    setContacts: (state, action) => {
      state.chatContact = action.payload;
    },
    setSelectedUser: (state, action) => {
      // console.log(action.payload);
      state.selectedUser = action.payload;
    },
    fetchChatMemberAsync: (state) => {
      if (state.allMembers.length > 0) {
        const foundMember = state.allMembers.find(
          (m) => m.id === currentUserId
        );
        if (foundMember) {
          state.currentUser = foundMember;
        } else {
          state.currentUser = {}; // Assign an empty object or a suitable default value
        }
        state.members = state.allMembers;
      }
    },

    setMemberStatus: (state, action) => {
      const onlineUserIds = action.payload; // Array of user IDs that are online

      state.members = state.members.map((member) => ({
        ...member,
        online: onlineUserIds.includes(member.id), // Set online if ID is in the array
      }));
    },

    setMemberStatusById: (state, action) => {
      const { online, userId } = action.payload; // `userId` is a single ID, and `online` is a boolean
      state.members = state.members.map((member) => ({
        ...member,
        online: member.id === Number(userId) ? online : member.online, // Set online if the ID matches userId, else keep the current status
      }));
    },

    // fetchChatAsync: (state) => {
    //   if (state.chats.length > 0) {
    //     var chat = state.chats.filter((x: ChatConversation) =>
    //       x.users.includes(currentUserId)
    //     );
    //     var selectedUser = state.chats[0].users.find(
    //       (x: any) => x !== currentUserId
    //     );

    //     if (state.allMembers.length > 0) {
    //       state.chats = chat;
    //       state.selectedUser = state.allMembers.find(
    //         (x: AllMembersType) => x.id === selectedUser
    //       );
    //     }
    //   }
    // },

    getMembersSuccess: (state, action) => {
      state.currentUser = action.payload[0];
      state.members = action.payload;
    },
    getChatsSuccess: (state, action) => {
      if (state.members.length > 0) {
        state.chats = action.payload.chats;
      }
    },
    searchMember: (state, action) => {
      const keyword = action.payload.toLowerCase();
      state.members = state.allMembers.filter((member: AllMembersType) =>
        member.name.toLowerCase().includes(keyword)
      );
    },
    changeChat: (state, action) => {
      let newUser = state.members.find(
        (x: AllMembersType) => x.id === action.payload
      );
      state.selectedUser = newUser;
      state.isGroup = false;
    },

    changeGroupChat: (state, action) => {
      let newGroup = state.groups.find(
        (x: any) => x.groupId === action.payload
      );

      if (newGroup) {
        const { users } = newGroup; // Access users from newGroup, not state.groups
        state.selectedUser = users.map((u: any) => u.id);
        state.selectedGroup = newGroup;
        state.isGroup = true;
      }
    },

    createNewGroupChatAsync: (state, action) => {
      let groupChat = {
        id: action.payload.group.groupId, // Group ID from payload
        users: [
          action.payload.currentUserId,
          ...action.payload.group.users.map((u: any) => u.id), // Map user IDs from group
        ],
        messages: [],
        lastMessageTime: "-",
      };

      // Add the group chat to the beginning of the chats array
      state.chats.splice(0, 0, groupChat);

      // Set selected users for easy access
      if (state.allMembers.length > 0) {
        state.selectedUser = state.allMembers.filter((member) =>
          action.payload.group.users.some((user: any) => user.id === member.id)
        );
      }

      state.selectedUser = action.payload.group.users.map((u: any) => u.id);
      state.selectedGroup = action.payload.group;
      state.isGroup = true;
    },

    createNewChatAsync: (state, action) => {
      let conversation = {
        id: action.payload.chats.length + 1,
        users: [action.payload.currentUserId, action.payload.selectedUserId],
        lastMessageTime: "-",
        messages: [],
      };
      state.chats.splice(0, 0, conversation);
      if (state.allMembers.length > 0) {
        let selctedUser = state.allMembers.find(
          (x: AllMembersType) => x.id === action.payload.selectedUserId
        );
        state.selectedUser = selctedUser;
      }
      state.selectedGroup = null;
      state.isGroup = false;
    },

    sendMessageAsync: (state, action) => {
      const chat = state.isGroup
        ? state.chats.find(
            (c: ChatConversation) =>
              c.id === `GROUP-${state.selectedGroup.groupId}`
          )
        : state.chats.find(
            (x: ChatConversation) =>
              x.users.includes(action.payload.currentUserId) &&
              x.users.includes(action.payload.selectedUserId)
          );

      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const period = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12; // Convert to 12-hour format, with 12 instead of 0

      const time = `${formattedHours}:${minutes} ${period}`;

      if (chat) {
        chat.messages.push({
          sender: action.payload.currentUserId,
          time: time,
          text: action.payload.messageInput,
          status: true,
          type: action.payload.type,
          meta: action.payload.meta,
        });
        chat.lastMessageTime = time;
        chat.online = true;

        // Move the chat to the top of the chats list
        state.chats = [
          chat,
          ...state.chats.filter((x: ChatConversation) => x.id !== chat.id),
        ];

        // Update selected user if it's a private chat and allMembers is populated
        if (state.allMembers.length > 0 && !state.isGroup) {
          let selectedUser = state.allMembers.find(
            (x: AllMembersType) => x.id === action.payload.selectedUserId
          );
          state.selectedUser = selectedUser || null;
        }
      }
    },

    replyByUserAsync: (state, action) => {
      const chat = state.isGroup
        ? state.chats.find(
            (c: ChatConversation) =>
              c.id === `GROUP-${state.selectedGroup.groupId}`
          )
        : state.chats.find(
            (x: ChatConversation) =>
              x.users.includes(action.payload.currentUserId) &&
              x.users.includes(action.payload.selectedUserId)
          );

      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const period = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12; // Convert to 12-hour format, with 12 instead of 0
      const time = `${formattedHours}:${minutes} ${period}`;

      if (chat) {
        chat.messages.push({
          sender: action.payload.selectedUserId,
          time: time,
          text: action.payload.replyMessage,
          status: true,
          type: action.payload.type,
          meta: action.payload.meta,
          PK: action.payload.PK,
          SK: action.payload.SK,
        });
        chat.lastMessageTime = time;
        let chats_Data = state.chats.filter(
          (x: ChatConversation) => x.id !== chat.id
        );
        chats_Data.splice(0, 0, chat);
      }
    },
    incrementUnreadCount: (state) => {
      state.unreadMessageCount += 1;
    },
    resetUnreadCount: (state) => {
      state.unreadMessageCount = 0;
    },
  },
});

export default ChatSlice.reducer;
export const {
  setLoading,
  setContacts,
  setAllMembers,
  searchMember,
  setMemberStatus,
  setMemberStatusById,
  setToggleSideBar,
  setSocket,
  setConnected,
  setConnectionId,
  setChats,
  clearChat,
  deleteChat,
  setGroups,
  newGroupAdded,
  updateGroup,
  deleteGroup,
  changeChat,
  changeGroupChat,
  sendMessageAsync,
  replyByUserAsync,
  createNewChatAsync,
  createNewGroupChatAsync,
  setSelectedUser,
  fetchChatMemberAsync,
  // fetchChatAsync,
  getMembersSuccess,
  getChatsSuccess,
  UpdateFriends,
  UpdateRequests,
  UpdateBlocked,
  setStores,
  setStoreChatRequests,
  incrementUnreadCount,
  resetUnreadCount,
} = ChatSlice.actions;
